/* -------------------------------------------------------------------------
 *  map-manager.js
 *  ---------------------------------------------------------------
 *  Shared Mapbox layer for LEVA configurators (fence, pool, main, ...).
 *
 *  USAGE
 *  -----
 *    1. Page must already have loaded Mapbox GL JS (window.mapboxgl).
 *    2. Page must include the markup contract (#cf-mapbox, #cf-btn-map,
 *       #cf-map-controls + children, #cf-map-modal-backdrop + children).
 *       See README in the lib folder or copy from configurator-fence.liquid.
 *    3. Call:
 *
 *         window.MapManager.init({
 *           token:         '<MAPBOX_TOKEN>',                // required
 *           i18n:          { rotation: 'Rotation', ... },   // optional, all keys have English fallbacks
 *           storageKey:    'levaMap:p123',                  // shared persistence key for this project
 *           engine: {                                       // refs into host Three.js setup
 *             camera, controls, orthoCamera, orthoControls
 *           },
 *           getView:       () => state.view,                // returns '2d-top' | '3d'
 *           getRootSize:   () => ({ w, h }),                // returns canvas wrapper size
 *           onChange:      () => host.scheduleAutoSave(),   // called on user-driven map change
 *           requestRender: () => host.requestFrame()        // optional, no-op if absent
 *         });
 *
 *  V2 OPTIONS (all optional; omitting them reproduces v1 behaviour exactly)
 *  -----------------------------------------------------------------------
 *    autoMarkup:     true (default) - inject any missing map markup into
 *                    #cf-root. Elements already present in the host Liquid are
 *                    never touched, so existing hosts are unaffected. Pass
 *                    false to opt out entirely.
 *    rootId:         id of the wrapper to inject into      (default 'cf-root')
 *    canvasId:       map is inserted before this element   (default 'cf-canvas')
 *    pad:            include the 4-way pan pad in injected markup. Default
 *                    false - panning by finger/arrow is enough. Hosts that
 *                    already have the pad in their Liquid keep it either way,
 *                    since existing markup is never touched.
 *    lock:           show the lock button                  (default false)
 *    getLocked:      () => bool   - host supplies initial lock state
 *    onLockChange:   (locked) => {} - host stores the new lock state
 *    modalExtraHTML: string - extra markup injected inside the address modal
 *                    (e.g. a climate-zone select); host wires it up itself
 *    onMarkupReady:  (root) => {} - called after injection so the host can bind
 *                    whatever it added via modalExtraHTML
 *
 *  All DOM binding is defensive: a missing element is skipped, never thrown on.
 *
 *  WHAT IT DOES
 *  ------------
 *    - Adds an address-search modal that geocodes via Mapbox Geocoding API.
 *    - Loads a Mapbox map *under* the host's Three.js canvas at a zoom level
 *      that locks 1 m of map = 1 m of grid.
 *    - 4-direction pan pad + vertical rotation slider + 2-style switch.
 *    - In 3D view: tilts the map to follow the host's perspective camera
 *      (bearing, pitch, zoom recomputed from camera state) and adds 3D
 *      buildings + sky layers.
 *    - Persists center/bearing/style under storageKey; restore() replays it.
 *    - Cross-tab sync: a window 'storage' event handler reloads the map
 *      state when another tab writes the same storageKey.
 *
 *  NOT IN THIS FILE
 *  ----------------
 *    - The HTML markup (kept in host Liquid for {{ ... | t }} translations).
 *    - The .cf-map-modal-backdrop class -- that base class is reused by
 *      non-map modals (e.g. trial gate) and stays in the host CSS.
 * ------------------------------------------------------------------------- */

(function (global) {
  'use strict';

  // Defaults for i18n keys -- host can override any via init({i18n: {...}}).
  const I18N_DEFAULTS = {
    rotation:           'Rotation',
    streets:            'Roadmap',
    satellite:          'Satellite',
    addressPlaceholder: 'Address, city or place...',
    load:               'Load',
    cancel:             'Cancel',
    modalTitle:         'Map location',
    addBackground:      'Add map background',
    changeLocation:     'Change map location',
    lockMap:            'Lock map position',
    streetsShort:       'MAP',
    satelliteShort:     'SAT'
  };

  const MM = {
    // Bind-time config (filled by init)
    _deps:        null,
    _i18n:        I18N_DEFAULTS,
    _storageKey:  null,
    MAPBOX_TOKEN: null,
    STYLE_STREETS:   'mapbox://styles/mapbox/light-v11',
    STYLE_SATELLITE: 'mapbox://styles/mapbox/satellite-streets-v12',

    // Runtime state
    map:          null,
    currentStyle: 'streets',
    center:       null,     // [lng, lat]
    zoomLevel:    null,
    bearing:      0,
    address:      '',

    _geocodeAbort:    null,
    _suggestionItems: [],

    _opts:   {},
    _locked: false,

  _el(id) { return document.getElementById(id); },

  _on(id, ev, fn) {
    const el = document.getElementById(id);
    if (el) el.addEventListener(ev, fn);
    return el;
  },

  _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  },

  _ensureMarkup() {
    const o = this._opts || {};
    if (o.autoMarkup === false) return;
    const root = document.getElementById(o.rootId || 'cf-root');
    if (!root) return;
    const t = this._i18n;
    const esc = this._esc;
    const canvas = document.getElementById(o.canvasId || 'cf-canvas');
    const put = (node) => { if (canvas) root.insertBefore(node, canvas); else root.appendChild(node); };

    if (!document.getElementById('cf-mapbox')) {
      const d = document.createElement('div');
      d.id = 'cf-mapbox';
      put(d);
    }
    if (!root.querySelector('.cf-map-fade')) {
      const f = document.createElement('div');
      f.className = 'cf-map-fade';
      put(f);
    }
    if (!document.getElementById('cf-map-credit')) {
      const c = document.createElement('div');
      c.id = 'cf-map-credit';
      c.className = 'cf-map-credit';
      c.textContent = '\u00A9 Mapbox \u00A9 OpenStreetMap';
      root.appendChild(c);
    }
    if (!document.getElementById('cf-map-controls')) {
      const padHTML = (o.pad !== true) ? '' :
        '<div class="cf-map-pad">' +
        '  <button id="cf-map-pad-up" class="cf-map-pad-btn cf-map-pad-up" type="button">\u25B2</button>' +
        '  <button id="cf-map-pad-left" class="cf-map-pad-btn cf-map-pad-left" type="button">\u25C0</button>' +
        '  <button id="cf-map-pad-right" class="cf-map-pad-btn cf-map-pad-right" type="button">\u25B6</button>' +
        '  <button id="cf-map-pad-down" class="cf-map-pad-btn cf-map-pad-down" type="button">\u25BC</button>' +
        '</div>';
      const lockHTML = !o.lock ? '' :
        '<button id="cf-map-lock" class="cf-map-style-btn cf-map-lock-btn" type="button" title="' + esc(t.lockMap) + '" aria-pressed="false">' +
        '  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '    <rect x="5" y="11" width="14" height="9" rx="2"/>' +
        '    <path d="M8 11V7a4 4 0 0 1 8 0v4" id="cf-map-lock-shackle"/>' +
        '  </svg>' +
        '</button>';
      const ctrls = document.createElement('div');
      ctrls.id = 'cf-map-controls';
      ctrls.className = 'cf-map-controls cf-map-stacked';
      ctrls.innerHTML =
        '<div class="cf-map-rotate-vert">' +
        '  <input type="range" id="cf-map-rotate" class="cf-map-rotate-slider" min="0" max="360" step="1" value="0" aria-label="' + esc(t.rotation) + '">' +
        '</div>' +
        '<div class="cf-map-right-col">' +
        '  <div class="cf-map-style-row">' +
        '    <button id="cf-map-style-streets" class="cf-map-style-btn active" type="button">' + esc(t.streetsShort || 'MAP') + '</button>' +
        '    <button id="cf-map-style-satellite" class="cf-map-style-btn" type="button">' + esc(t.satelliteShort || 'SAT') + '</button>' +
        lockHTML +
        '  </div>' +
        padHTML +
        '</div>';
      root.appendChild(ctrls);
    }
    if (!document.getElementById('cf-map-modal-backdrop')) {
      const m = document.createElement('div');
      m.id = 'cf-map-modal-backdrop';
      m.className = 'cf-map-modal-backdrop';
      m.innerHTML =
        '<div class="cf-map-modal" role="dialog" aria-modal="true">' +
        '  <h3>' + esc(t.modalTitle) + '</h3>' +
        '  <div class="cf-map-modal-input-wrap">' +
        '    <input id="cf-map-address-input" type="text" placeholder="' + esc(t.addressPlaceholder) + '" autocomplete="off">' +
        '    <div id="cf-map-suggestions" class="cf-map-modal-suggestions"></div>' +
        '  </div>' +
        (o.modalExtraHTML || '') +
        '  <div class="cf-map-modal-buttons">' +
        '    <button id="cf-map-modal-cancel" class="cf-map-modal-btn cf-map-modal-btn-cancel" type="button">' + esc(t.cancel) + '</button>' +
        '    <button id="cf-map-modal-ok" class="cf-map-modal-btn cf-map-modal-btn-ok" type="button" disabled>' + esc(t.load) + '</button>' +
        '  </div>' +
        '</div>';
      root.appendChild(m);
    }
    if (typeof o.onMarkupReady === 'function') o.onMarkupReady(root);
  },

  _hidePadUnlessWanted() {
    if (this._opts && this._opts.pad === true) return;
    ['cf-map-pad-up', 'cf-map-pad-down', 'cf-map-pad-left', 'cf-map-pad-right'].forEach((id) => {
      const el = this._el(id);
      if (el && el.style) el.style.display = 'none';
    });
    const grid = document.querySelector ? document.querySelector('.cf-map-pad') : null;
    if (grid && grid.style) grid.style.display = 'none';
  },

  setLocked(v) {
    this._locked = !!v;
    const b = document.getElementById('cf-map-lock');
    if (b) {
      b.classList.toggle('locked', this._locked);
      b.setAttribute('aria-pressed', this._locked ? 'true' : 'false');
    }
    const sh = document.getElementById('cf-map-lock-shackle');
    if (sh) sh.setAttribute('d', this._locked ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 8 0');
  },

  isLocked() { return !!this._locked; },

  init() {
    if (!window.mapboxgl) {
      console.warn('[MapManager] mapbox-gl not loaded');
      return;
    }
    mapboxgl.accessToken = this.MAPBOX_TOKEN;

    this._ensureMarkup();
    this._hidePadUnlessWanted();

    this._on('cf-btn-map', 'click', () => {
      this._openModal();
    });

    const backdrop = this._el('cf-map-modal-backdrop');
    const input = this._el('cf-map-address-input');
    const okBtn = this._el('cf-map-modal-ok');
    const sugg = this._el('cf-map-suggestions');

    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this._closeModal();
      });
    }
    this._on('cf-map-modal-cancel', 'click', () => this._closeModal());

    let debounceT = null;
    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounceT);
        const q = input.value.trim();
        if (q.length < 3) {
          if (sugg) sugg.classList.remove('open');
          if (okBtn) okBtn.disabled = true;
          return;
        }
        debounceT = setTimeout(() => this._geocode(q), 300);
      });
    }

    this._on('cf-map-modal-ok', 'click', () => {
      const selected = this._suggestionItems.find(s => s.selected) || this._suggestionItems[0];
      if (!selected) return;
      this._loadMap(selected.center, selected.label);
      this._closeModal();
    });

    this._on('cf-map-style-streets', 'click', () => this._setStyle('streets'));
    this._on('cf-map-style-satellite', 'click', () => this._setStyle('satellite'));

    this._on('cf-map-lock', 'click', () => {
      this.setLocked(!this._locked);
      const cb = this._opts && this._opts.onLockChange;
      if (typeof cb === 'function') cb(this._locked);
      if (this._deps && this._deps.onChange) this._deps.onChange();
    });
    if (this._opts && typeof this._opts.getLocked === 'function') this.setLocked(this._opts.getLocked());

    // Directional pan pad (4 arrows). Step size comes from the host (typically
    // ~5 % of the land extent) so the meters-per-click scales with the scene.
    // Fallback to 2 m if host didn't provide a callback.
    const padStep = () => {
      if (typeof this._deps.getPadStep === 'function') return this._deps.getPadStep();
      return 2;
    };
    this._on('cf-map-pad-up', 'click',    () => this._panMeters(0, -padStep()));
    this._on('cf-map-pad-down', 'click',  () => this._panMeters(0,  padStep()));
    this._on('cf-map-pad-left', 'click',  () => this._panMeters(-padStep(), 0));
    this._on('cf-map-pad-right', 'click', () => this._panMeters( padStep(), 0));

    // Rotate slider -- silent (no value label)
    const rotateInput = this._el('cf-map-rotate');
    const updateRotateFill = () => {
      if (!rotateInput) return;
      const pct = (parseFloat(rotateInput.value) / 360) * 100;
      rotateInput.style.setProperty('--cf-fill', pct + '%');
    };
    if (rotateInput) {
      rotateInput.addEventListener('input', () => {
        const deg = parseInt(rotateInput.value, 10);
        this.bearing = deg;
        if (this.map) {
          if (this._deps.getView() === '3d') {
            // In 3D the bearing is composed of baseBearing + scene azimuth;
            // recompute via sync rather than overwriting with raw slider value.
            this._syncMapTo3D(false);
          } else {
            this.map.setBearing(deg);
          }
        }
        updateRotateFill();
      });
    }
    updateRotateFill();

    // Initial button state
    this._updateSidebarButton();
  },

  _openModal() {
    const bd = this._el('cf-map-modal-backdrop');
    if (bd) bd.classList.add('open');
    const input = document.getElementById('cf-map-address-input');
    input.value = this.address || '';
    setTimeout(() => input.focus(), 50);
  },
  _closeModal() {
    const bd2 = this._el('cf-map-modal-backdrop');
    if (bd2) bd2.classList.remove('open');
    const sg2 = this._el('cf-map-suggestions');
    if (sg2) sg2.classList.remove('open');
  },

  _geocode(query) {
    if (this._geocodeAbort) this._geocodeAbort.abort();
    this._geocodeAbort = new AbortController();
    const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      encodeURIComponent(query) + '.json?access_token=' + this.MAPBOX_TOKEN +
      '&autocomplete=true&limit=5&types=address,place,locality,neighborhood,region';
    fetch(url, { signal: this._geocodeAbort.signal })
      .then(r => r.json())
      .then(data => {
        const sugg = document.getElementById('cf-map-suggestions');
        sugg.innerHTML = '';
        this._suggestionItems = (data.features || []).map((f, i) => ({
          label: f.place_name,
          center: f.center,  // [lng, lat]
          selected: i === 0
        }));
        if (!this._suggestionItems.length) {
          sugg.classList.remove('open');
          document.getElementById('cf-map-modal-ok').disabled = true;
          return;
        }
        this._suggestionItems.forEach((s, i) => {
          const div = document.createElement('div');
          div.className = 'cf-map-modal-sugg-item';
          div.textContent = s.label;
          div.addEventListener('click', () => {
            this._suggestionItems.forEach(it => it.selected = false);
            s.selected = true;
            const ai = this._el('cf-map-address-input');
            if (ai) ai.value = s.label;
            sugg.classList.remove('open');
            document.getElementById('cf-map-modal-ok').disabled = false;
          });
          sugg.appendChild(div);
        });
        sugg.classList.add('open');
        document.getElementById('cf-map-modal-ok').disabled = false;
      })
      .catch(e => {
        if (e.name !== 'AbortError') console.warn('[MapManager] geocode failed', e);
      });
  },

  // Compute the Mapbox zoom level that matches our Three.js ortho camera scale,
  // so that 1 m on the grid = 1 m on the satellite imagery.
  //
  // Mapbox metersPerPixel = 156543.03392 x cos(lat) / 2^zoom
  // We need: ourMetersPerPixel = Mapbox metersPerPixel
  //   ->  zoom = log2(156543.03392 x cos(lat) / ourMetersPerPixel)
  _computeZoomForLat(lat) {
    const wrap = document.getElementById('cf-root');
    const canvasW = wrap.clientWidth;
    // Three.js ortho frustum width in world meters:
    const cam = this._deps.orthoCamera;
    const frustumW = (cam.right - cam.left) / cam.zoom;
    const ourMpp = frustumW / canvasW;
    const z = Math.log2(156543.03392 * Math.cos(lat * Math.PI / 180) / ourMpp);
    return z;
  },

  _loadMap(center, label) {
    this.center = center;
    this.address = label;
    this.zoomLevel = this._computeZoomForLat(center[1]);

    if (this.map) {
      // Already exists -- just recenter
      this.map.jumpTo({ center, zoom: this.zoomLevel, bearing: this.bearing });
    } else {
      const mapDiv = document.getElementById('cf-mapbox');
      mapDiv.classList.add('cf-map-visible');
      const cr = this._el('cf-map-credit');
      if (cr) cr.classList.add('cf-map-visible');
      const rt = this._el('cf-root');
      if (rt) rt.classList.add('cf-has-map');
      const fade = document.querySelector('.cf-map-fade');
      if (fade) fade.classList.add('cf-map-visible');

      this.map = new mapboxgl.Map({
        container: 'cf-mapbox',
        style: this.STYLE_STREETS,
        center,
        zoom: this.zoomLevel,
        bearing: this.bearing,
        pitch: 0,
        attributionControl: false,
        interactive: false  // user controls map ONLY via arrow pad + rotate slider
      });
      // Disable every interaction handler explicitly (belt + suspenders)
      this.map.dragPan.disable();
      this.map.scrollZoom.disable();
      this.map.boxZoom.disable();
      this.map.dragRotate.disable();
      this.map.keyboard.disable();
      this.map.doubleClickZoom.disable();
      this.map.touchZoomRotate.disable();

      this._updateSidebarButton();

      // Canvas always captures pointer events -- map is purely a visual layer
      // controlled by the arrow pad and rotate slider.
      const cv = this._el('cf-canvas');
      if (cv) cv.style.pointerEvents = 'auto';

      // CRITICAL: keep map zoom in lock-step with our ortho camera so 1 grid m = 1 map m.
      // We listen to orthoControls 'change' (fires on wheel zoom, pinch, programmatic fitCamera)
      // and recompute the matching Mapbox zoom level.
      const syncZoom = () => {
        if (!this.map || !this.center) return;
        if (this._deps.getView() !== '2d-top') return;   // 3D has its own sync path
        const z = this._computeZoomForLat(this.center[1]);
        if (Math.abs(z - this.zoomLevel) > 0.005) {
          this.zoomLevel = z;
          this.map.setZoom(z);
        }
      };
      this._deps.orthoControls.addEventListener('change', syncZoom);
      // Also after fitCamera (which changes zoom programmatically without orthoControls event)
      window.addEventListener('resize', () => setTimeout(syncZoom, 50));
      // Initial sync (after first frame, ortho camera is fitted)
      setTimeout(syncZoom, 100);

      // --- 3D sync: when perspective camera orbits, mirror the move on the map.
      // Fires only when controls actually move (no idle ticks). Cheap enough to
      // run unthrottled -- map.jumpTo is a transform update, not a re-render of tiles.
      this._deps.controls.addEventListener('change', () => {
        if (this._deps.getView() === '3d' && this.map && this.center) this._syncMapTo3D(false);
      });

      // Install 3D layers as soon as the style is ready (also re-fires after _setStyle).
      // If we're already in 3D when the map loads or the style swaps, also push the
      // current Three.js camera state to Mapbox right away.
      this.map.on('style.load', () => {
        this._install3DLayers();
        if (this._deps.getView() === '3d') this._syncMapTo3D(false);
      });

      // Re-fit map size when canvas resizes
      new ResizeObserver(() => {
        if (this.map) {
          this.map.resize();
          syncZoom();
        }
      }).observe(mapDiv);
    }

    const mc = this._el('cf-map-controls');
    if (mc) mc.classList.add('open');
    this._deps.onChange();
  },

  _setStyle(s) {
    if (!this.map) return;
    this.currentStyle = s;
    const url = s === 'satellite' ? this.STYLE_SATELLITE : this.STYLE_STREETS;
    this.map.setStyle(url);
    const bs = this._el('cf-map-style-streets');
    if (bs) bs.classList.toggle('active', s === 'streets');
    const bt = this._el('cf-map-style-satellite');
    if (bt) bt.classList.toggle('active', s === 'satellite');
    this._deps.onChange();
  },

  // --- 3D extensions ------------------------------------------
  // Install 3D buildings + sky layer. Terrain is intentionally NOT enabled
  // even though the DEM source could be added -- Mapbox terrain renders ground
  // at real elevation above sea level (e.g. ~200 m in Brno), but the Three.js
  // scene's fence/footprint sits at y=0. The two reference frames mismatch
  // and the fence appears to float above the elevated map surface. Without
  // terrain, both the map ground and the scene plane share sea level, so the
  // fence visually rests on the ground. 3D buildings extrude from this flat
  // base which is fine for context.
  _install3DLayers() {
    if (!this.map) return;
    const m = this.map;
    // 1. Sky layer -- visible only at high pitch, but harmless otherwise
    try {
      if (!m.getLayer('cf-sky')) {
        m.addLayer({
          id: 'cf-sky',
          type: 'sky',
          paint: {
            'sky-type': 'atmosphere',
            'sky-atmosphere-sun-intensity': 5
          }
        });
      }
    } catch (e) { console.warn('[MapManager] sky layer add failed', e); }
    // 2. 3D buildings -- both Light and Satellite-streets styles expose
    // 'composite' source with 'building' source-layer.
    try {
      if (!m.getLayer('cf-3d-buildings') && m.getSource('composite')) {
        m.addLayer({
          id: 'cf-3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 14,
          paint: {
            'fill-extrusion-color': '#d6cfc2',
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': ['get', 'min_height'],
            'fill-extrusion-opacity': 0.85
          }
        });
      }
    } catch (e) { console.warn('[MapManager] 3D buildings layer add failed', e); }

    // Apply current view's layer visibility (newly-added layers start visible)
    this._set3DLayerVisibility(this._deps.getView() === '3d');
  },

  // Compute Mapbox pitch/bearing/zoom that match the current Three.js
  // perspective camera state. Center is left untouched -- the map stays anchored
  // wherever the user placed it; only the viewing angle follows the orbit.
  _computeMapStateFor3D() {
    if (!this._deps.camera || !this._deps.controls || !this.center) return null;
    const target = this._deps.controls.target;
    const pos = this._deps.camera.position;
    const offX = pos.x - target.x;
    const offY = pos.y - target.y;
    const offZ = pos.z - target.z;
    const horiz = Math.sqrt(offX*offX + offZ*offZ);
    const dist = Math.sqrt(horiz*horiz + offY*offY);
    if (dist < 0.01) return null;

    // Pitch -- Three.js elevation above horizon vs. Mapbox tilt-from-top-down.
    // Mapbox max pitch is 85 deg; clamp to 0..85 to avoid jitter near zenith.
    const elev = Math.atan2(offY, horiz);
    const pitch = Math.max(0, Math.min(85, 90 - elev * 180 / Math.PI));

    // Bearing -- compass direction the camera is facing.
    // Scene convention: -Z = north, +X = east. Camera look direction = -offset.
    // East component of look = -offX. North component of look = offZ (since
    // north = -Z, the north-component of (x,z) is -z, and z here is -offZ).
    // bearingDeg = atan2(eastComp, northComp). Add user's slider baseBearing
    // (which rotated the satellite imagery to align with scene north in 2D).
    const sceneBearingDeg = Math.atan2(-offX, offZ) * 180 / Math.PI;
    const bearing = (this.bearing || 0) + sceneBearingDeg;

    // Zoom -- match Three.js perspective frustum at the target plane so 1 m on
    // scene ~ 1 m on map at the center of screen.
    const wrap = document.getElementById('cf-root');
    const canvasH = wrap ? wrap.clientHeight : 600;
    const fovRad = this._deps.camera.fov * Math.PI / 180;
    const heightAtTarget = 2 * dist * Math.tan(fovRad / 2);
    const mpp = heightAtTarget / canvasH;
    const lat = this.center[1];
    const zoom = Math.log2(156543.03392 * Math.cos(lat * Math.PI / 180) / mpp);

    return { pitch, bearing, zoom };
  },

  _syncMapTo3D(animate) {
    if (!this.map || !this.center) return;
    const s = this._computeMapStateFor3D();
    if (!s) return;
    if (animate) {
      this.map.easeTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom, duration: 400 });
    } else {
      this.map.jumpTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom });
    }
  },

  _syncMapTo2D(animate) {
    if (!this.map || !this.center) return;
    // Flat top-down view; restore the user's base bearing and ortho-matched zoom.
    const z = this._computeZoomForLat(this.center[1]);
    this.zoomLevel = z;
    if (animate) {
      this.map.easeTo({ pitch: 0, bearing: this.bearing || 0, zoom: z, duration: 400 });
    } else {
      this.map.jumpTo({ pitch: 0, bearing: this.bearing || 0, zoom: z });
    }
  },

  // Toggle the 3D-only layers (buildings, sky) so the 2D top-down view looks
  // identical to the original flat map.
  _set3DLayerVisibility(visible) {
    if (!this.map) return;
    const v = visible ? 'visible' : 'none';
    for (const id of ['cf-3d-buildings', 'cf-sky']) {
      try {
        if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', v);
      } catch(e){}
    }
  },

  _updateSidebarButton() {
    const btn = document.getElementById('cf-btn-map');
    if (!btn) return;
    // Always shows the map icon. Olive background indicates a map is currently loaded.
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z"/><path d="M9 4v16"/><path d="M15 6v16"/></svg>';
    if (this.map) {
      btn.classList.add('cf-map-btn-active');
      btn.title = this._i18n.changeLocation;
    } else {
      btn.classList.remove('cf-map-btn-active');
      btn.title = this._i18n.addBackground;
    }
  },

  // Pan map by N meters in screen-space direction (uses current bearing).
  // Used by 4 directional arrow buttons on the controls panel.
  _panMeters(dirX, dirZ) {
    if (!this.map) return;
    // Convert meters to pixels at the current zoom + latitude.
    // metersPerPixel = 156543.03392 x cos(lat) / 2^zoom
    const lat = this.center[1];
    const zoom = this.map.getZoom();
    const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
    const dx = dirX / metersPerPixel;
    const dy = dirZ / metersPerPixel;
    // map.panBy uses screen-space pixel offset and handles bearing internally,
    // so arrows always match what's visible on screen regardless of rotation.
    this.map.panBy([dx, dy], { animate: false });
    // Update our cached center from the map's new state
    const c = this.map.getCenter();
    this.center = [c.lng, c.lat];
    this._deps.onChange();
  },

  _removeMap() {
    if (this.map) { this.map.remove(); this.map = null; }
    this.center = null;
    this.zoomLevel = null;
    this.bearing = 0;
    this.address = '';
    const mb = this._el('cf-mapbox');
    if (mb) mb.classList.remove('cf-map-visible');
    const cr2 = this._el('cf-map-credit');
    if (cr2) cr2.classList.remove('cf-map-visible');
    const rt2 = this._el('cf-root');
    if (rt2) rt2.classList.remove('cf-has-map');
    const fadeEl = document.querySelector('.cf-map-fade');
    if (fadeEl) fadeEl.classList.remove('cf-map-visible');
    const mc2 = this._el('cf-map-controls');
    if (mc2) mc2.classList.remove('open');
    const rotateInput = this._el('cf-map-rotate');
    if (rotateInput) rotateInput.value = 0;
    const cv2 = this._el('cf-canvas');
    if (cv2) cv2.style.pointerEvents = 'auto';
    this._updateSidebarButton();
    if (this._deps && this._deps.onChange) this._deps.onChange();
  },

  // Serialization payload -- host calls MapManager.serialize() inside its own
  // save-to-storage routine and stores the result alongside its other state.
  // IMPORTANT: bearing is the slider value (this.bearing), NOT map.getBearing().
  // In 3D the live map bearing is base + scene azimuth -- saving the composed
  // value and restoring it as base would double-rotate. Zoom is intentionally
  // omitted; _loadMap recomputes it from the recipient's ortho frustum so the
  // 1m-grid-equals-1m-map invariant holds across different screen sizes.
  serialize() {
    if (!this.map || !this.center) return null;
    return {
      center: this.center,
      bearing: this.bearing || 0,
      style: this.currentStyle,
      address: this.address
    };
  },
  restore(data) {
    if (!data || !data.center) return;
    this.bearing = data.bearing || 0;
    const rotateInput = document.getElementById('cf-map-rotate');
    if (rotateInput) {
      rotateInput.value = Math.round(this.bearing);
      // Refresh the slider's --cf-fill so the rotated knob position matches
      const pct = (this.bearing / 360) * 100;
      rotateInput.style.setProperty('--cf-fill', pct + '%');
    }
    this._loadMap(data.center, data.address || '');
    if (data.style === 'satellite') this._setStyle('satellite');
  },

  // --- Persistence ---------------------------------------------
  // Storage is the lib's own concern when storageKey is set. Hosts can still
  // also include serialize() output in their own state blob, but persist()
  // makes the map self-contained for cross-tab sync.
  _suppressStorageEvent: false,
  persist() {
    if (!this._storageKey) return;
    const data = this.serialize();
    if (!data) return;
    try {
      this._suppressStorageEvent = true;
      localStorage.setItem(this._storageKey, JSON.stringify(data));
      // Cookie-style reset on next tick -- the storage event fires only in
      // OTHER tabs, but this guards against listeners we attach later.
      setTimeout(() => { this._suppressStorageEvent = false; }, 0);
    } catch (e) { /* quota or unavailable */ }
  },
  loadFromStorage() {
    if (!this._storageKey) return false;
    try {
      const raw = localStorage.getItem(this._storageKey);
      if (!raw) return false;
      const data = JSON.parse(raw);
      this.restore(data);
      return true;
    } catch (e) { return false; }
  },
  _onStorageEvent(e) {
    // Only react to changes to OUR key from another tab. The browser fires
    // 'storage' only in tabs other than the writer, so the suppression flag
    // is belt-and-suspenders for unusual environments (e.g. extensions).
    if (this._suppressStorageEvent) return;
    if (!this._storageKey || e.key !== this._storageKey) return;
    if (!e.newValue) return;
    try {
      const data = JSON.parse(e.newValue);
      // If center changed, do a full restore. Otherwise apply only bearing/style
      // to avoid the map flickering through a full reload.
      const oldCenter = this.center;
      if (!oldCenter || data.center[0] !== oldCenter[0] || data.center[1] !== oldCenter[1]) {
        this.restore(data);
      } else {
        if (typeof data.bearing === 'number') {
          this.bearing = data.bearing;
          if (this.map) this.map.setBearing(data.bearing);
          const ri = document.getElementById('cf-map-rotate');
          if (ri) {
            ri.value = Math.round(data.bearing);
            ri.style.setProperty('--cf-fill', (data.bearing / 360 * 100) + '%');
          }
        }
        if (data.style && data.style !== this.currentStyle) this._setStyle(data.style);
      }
    } catch (err) { /* malformed payload from other tab */ }
  }
  };

  // --- Public API ----------------------------------------------
  // init() validates dependencies and wires every DOM listener exactly once.
  // Safe to call again with new options (e.g. when storageKey changes); we
  // simply update internal refs without re-binding listeners.
  MM.init = (function (originalInit) {
    let initialized = false;
    return function (opts) {
      opts = opts || {};
      const hostOnChange = (opts.onChange || (() => {}));
      MM._deps         = opts.engine ? Object.assign({}, opts.engine, {
        getView:       opts.getView       || (() => '2d-top'),
        getRootSize:   opts.getRootSize   || (() => ({ w: window.innerWidth, h: window.innerHeight })),
        // Wrap host's onChange so persist() always runs first -- host can rely
        // on storage being current when its own save logic runs.
        onChange:      () => { MM.persist(); hostOnChange(); },
        requestRender: opts.requestRender || (() => {})
      }) : null;
      MM._i18n         = Object.assign({}, I18N_DEFAULTS, opts.i18n || {});
      MM._storageKey   = opts.storageKey || 'levaMap:default';
      MM.MAPBOX_TOKEN  = opts.token || MM.MAPBOX_TOKEN;
      MM._opts         = opts;
      if (initialized) return;            // listeners already bound
      initialized = true;
      window.addEventListener('storage', e => MM._onStorageEvent(e));
      originalInit.call(MM);
    };
  }(MM.init));

  /* ---------------------------------------------------------------------
   *  ConfiguratorStage
   *  -----------------
   *  The Three.js viewport every map-backed configurator sets up the same
   *  way: renderer, perspective + orthographic cameras, orbit controls with
   *  the 2D hard-lock, grid, recentre/fit, and 2D<->3D switching. Lives in
   *  this file because it is the half the map has to stay glued to -- the
   *  map only lines up with the scene because the camera behaves predictably.
   *
   *  It touches no Mapbox code, so a configurator with no map can still use
   *  it; the map simply stays dormant unless MapManager.init() is called.
   *
   *  USAGE
   *    ConfiguratorStage.init({
   *      rootId, canvasId,                 // default 'cf-root' / 'cf-canvas'
   *      fov, near, far,                   // perspective camera
   *      cameraPos: [x, y, z],
   *      target:    [x, y, z],
   *      minDistance, maxDistance,
   *      maxPolarAngle,                    // > PI/2 lets the user look from below
   *      enablePan, enableDamping, dampingFactor,
   *      lights: true,                     // false to light the scene yourself
   *      log: msg => {},
   *      onResize: () => {}
   *    });
   *
   *  Every option is optional; the defaults reproduce the container setup.
   * ------------------------------------------------------------------- */
  const Stage = {
    scene: null, camera: null, renderer: null, controls: null, group: null,
    persCam: null, persControls: null, orthoCam: null, orthoControls: null,
    view: '3d',
    _gridHelper: null, _gridAxes: null,
    _opts: {},

    init(opts) {
      const o = this._opts = Object.assign({
        rootId: 'cf-root', canvasId: 'cf-canvas',
        fov: 40, near: 0.1, far: 200,
        cameraPos: [7, 4, 8], target: [0, 1.2, 0],
        minDistance: 1.5, maxDistance: 40,
        maxPolarAngle: Math.PI * 0.85,
        enablePan: false, enableDamping: true, dampingFactor: 0.08,
        lights: true,
        log: function () {},
        onResize: null
      }, opts || {});

      const T = global.THREE;
      const root = document.getElementById(o.rootId);
      const canvas = document.getElementById(o.canvasId);
      if (!T || !root || !canvas) { o.log('stage init failed: THREE/canvas/root missing'); return false; }

      this.scene = new T.Scene();

      const W = root.clientWidth || 800;
      const H = root.clientHeight || 600;

      this.camera = new T.PerspectiveCamera(o.fov, W / H, o.near, o.far);
      this.camera.position.set(o.cameraPos[0], o.cameraPos[1], o.cameraPos[2]);
      this.camera.lookAt(o.target[0], o.target[1], o.target[2]);

      this.renderer = new T.WebGLRenderer({
        canvas: canvas, antialias: true, alpha: true,
        stencil: true, preserveDrawingBuffer: true
      });
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.localClippingEnabled = true;
      this.renderer.setSize(W, H, false);
      this.renderer.setPixelRatio(Math.min(global.devicePixelRatio || 1, 2));
      this.renderer.toneMapping = T.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.0;

      try {
        if (global.RoomEnvironment && T.PMREMGenerator) {
          const pm = new T.PMREMGenerator(this.renderer);
          this.scene.environment = pm.fromScene(new global.RoomEnvironment(), 0.04).texture;
          o.log('environment map ready (RoomEnvironment/PMREM)');
        }
      } catch (envErr) { o.log('env map skipped: ' + envErr.message); }

      this.controls = new global.OrbitControls(this.camera, canvas);
      this.controls.target.set(o.target[0], o.target[1], o.target[2]);
      this.controls.enablePan = o.enablePan;
      this.controls.enableDamping = o.enableDamping;
      this.controls.dampingFactor = o.dampingFactor;
      this.controls.minDistance = o.minDistance;
      this.controls.maxDistance = o.maxDistance;
      this.controls.maxPolarAngle = o.maxPolarAngle;
      this.controls.update();

      this.persCam = this.camera;
      this.persControls = this.controls;
      this.orthoCam = null;
      this.orthoControls = null;
      this.view = '3d';
      this._ensureOrtho();

      if (o.lights) {
        this.scene.add(new T.AmbientLight(0xffffff, 0.25));
        const sun = new T.DirectionalLight(0xfff4e0, 1.1);
        sun.position.set(6, 9, 5);
        this.scene.add(sun);
        const fill = new T.DirectionalLight(0xdfe8ff, 0.35);
        fill.position.set(-5, 5, -4);
        this.scene.add(fill);
      }

      this.group = new T.Group();
      this.scene.add(this.group);

      const self = this;
      this.renderer.setAnimationLoop(function () {
        self.controls.update();
        self.renderer.setClearColor(0x000000, 0);
        self.renderer.render(self.scene, self.camera);
      });

      function onResize() {
        const w = root.clientWidth, h = root.clientHeight;
        self.renderer.setSize(w, h, false);
        if (self.persCam) { self.persCam.aspect = w / h; self.persCam.updateProjectionMatrix(); }
        if (self.view === '2d') self.setView('2d');
        if (typeof o.onResize === 'function') o.onResize();
      }
      global.addEventListener('resize', onResize);
      if (global.ResizeObserver) new ResizeObserver(onResize).observe(root);

      o.log('stage ready (' + W + 'x' + H + ')');
      return true;
    },

    fitCameraToFootprint() {
      const T = global.THREE;
      if (!this.persCam || !this.group) return;
      const box = new T.Box3().setFromObject(this.group);
      if (box.isEmpty() || !isFinite(box.min.x)) return;
      const sphere = box.getBoundingSphere(new T.Sphere());
      const r = sphere.radius;
      if (!(r > 0)) return;
      const cam = this.persCam;
      const vFov = cam.fov * Math.PI / 180;
      const aspect = cam.aspect || 1.4;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      const minFov = Math.min(vFov, hFov);
      const dist = (r / Math.sin(minFov / 2)) * 0.85;
      const dir = new T.Vector3(1, 0.45, 1).normalize();
      cam.position.copy(sphere.center.clone().add(dir.multiplyScalar(dist)));
      cam.near = Math.max(0.05, dist / 200);
      cam.far = dist * 6;
      cam.updateProjectionMatrix();
      if (this.persControls) {
        this.persControls.target.copy(sphere.center);
        this.persControls.minDistance = dist * 0.3;
        this.persControls.maxDistance = dist * 3;
        this.persControls.update();
      } else {
        cam.lookAt(sphere.center);
      }
    },

    /* The 2D camera is deliberately immobile: the model stays centred and the
       map underneath it cannot drift. enabled is pinned to false through a
       non-configurable property so nothing can switch it back on by accident. */
    _ensureOrtho() {
      const T = global.THREE;
      if (this.orthoCam || !this.renderer || !T || !global.OrbitControls) return;
      const canvas = this.renderer.domElement;
      this.orthoCam = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 2000);
      this.orthoCam.up.set(0, 0, -1);
      this.orthoCam.position.set(0, 40, 0);
      this.orthoCam.lookAt(0, 0, 0);
      this.orthoControls = new global.OrbitControls(this.orthoCam, canvas);
      this.orthoControls.enableRotate = false;
      this.orthoControls.enableDamping = true;
      this.orthoControls.dampingFactor = 0.1;
      this.orthoControls.screenSpacePanning = true;
      this.orthoControls.mouseButtons = { LEFT: T.MOUSE.PAN, MIDDLE: T.MOUSE.DOLLY, RIGHT: T.MOUSE.PAN };
      this.orthoControls.touches = { ONE: T.TOUCH.PAN, TWO: T.TOUCH.DOLLY_PAN };

      try {
        Object.defineProperty(this.orthoControls, 'enabled', {
          get: function () { return false; },
          set: function () {},
          configurable: false
        });
      } catch (eOD) { this.orthoControls.enabled = false; }
    },

    setView(mode, focus) {
      const T = global.THREE;
      if (!this.renderer || !T) return;
      const canvas = this.renderer.domElement;
      const box = new T.Box3().setFromObject(this.group);
      let ctr = box.getCenter(new T.Vector3()), size = box.getSize(new T.Vector3());
      if (focus) {
        ctr = new T.Vector3(focus.cx, focus.cy || 1.2, focus.cz);
        size = new T.Vector3(focus.spanX, 3, focus.spanZ);
      }
      if (mode === '2d') {
        this._ensureOrtho();
        const aspect = (canvas.clientWidth || 1) / (canvas.clientHeight || 1);

        const reachX = Math.abs(ctr.x) + size.x / 2;
        const reachZ = Math.abs(ctr.z) + size.z / 2;
        const span = Math.max(reachX, reachZ) * 1.15 + 0.6;
        this.orthoCam.left = -span * aspect; this.orthoCam.right = span * aspect;
        this.orthoCam.top = span; this.orthoCam.bottom = -span;
        const up = Math.max(size.x, size.z) + 10;
        this.orthoCam.near = 0.01; this.orthoCam.far = up * 4;
        this.orthoCam.zoom = 1;
        this.orthoCam.position.set(0, up, 0.0001);
        this.orthoCam.up.set(0, 0, -1);
        this.orthoCam.lookAt(0, 0, 0);
        this.orthoCam.updateProjectionMatrix();
        this.orthoControls.target.set(0, 0, 0);
        this.orthoControls.update();
        this.persControls.enabled = false;
        this.orthoControls.enabled = false;
        this.camera = this.orthoCam;
        this.controls = this.orthoControls;
      } else {
        if (this.orthoControls) this.orthoControls.enabled = false;
        this.persControls.enabled = true;
        this.camera = this.persCam;
        this.controls = this.persControls;
        this.fitCameraToFootprint();
      }
      this.view = mode;
    },

    /* Ground grid + centre axes. Call again when the unit system flips. */
    rebuildGrid(opts) {
      const T = global.THREE;
      if (!T || !this.scene) return;
      const o = Object.assign({
        size: 25, step: 1.0,
        color1: 0xb9bfc6, color2: 0xe5e5e5,
        axes: true, axisColor: 0x8a93a0
      }, opts || {});

      if (this._gridHelper) {
        this.scene.remove(this._gridHelper);
        if (this._gridHelper.geometry) this._gridHelper.geometry.dispose();
        if (this._gridHelper.material) this._gridHelper.material.dispose();
        this._gridHelper = null;
      }
      const divisions = Math.round(o.size / o.step);
      this._gridHelper = new T.GridHelper(o.size, divisions, o.color1, o.color2);
      this._gridHelper.position.y = 0.005;
      this._gridHelper.rotation.set(0, 0, 0);
      this.scene.add(this._gridHelper);

      if (this._gridAxes) {
        this.scene.remove(this._gridAxes);
        if (this._gridAxes.geometry) this._gridAxes.geometry.dispose();
        if (this._gridAxes.material) this._gridAxes.material.dispose();
        this._gridAxes = null;
      }
      if (!o.axes) return;
      const ax = new T.BufferGeometry();
      ax.setAttribute('position', new T.Float32BufferAttribute([
        -o.size / 2, 0.008, 0, o.size / 2, 0.008, 0,
        0, 0.008, -o.size / 2, 0, 0.008, o.size / 2
      ], 3));
      this._gridAxes = new T.LineSegments(ax, new T.LineBasicMaterial({ color: o.axisColor }));
      this.scene.add(this._gridAxes);
    }
  };

  global.ConfiguratorStage = Stage;
  global.MapManager = MM;
}(typeof window !== 'undefined' ? window : globalThis));